// const calculateMaleAgePoints = (age: number): number => {
//     if (age < 30) return 0;
//     if (age < 40) return 3;
//     if (age < 50) return 8;
//     if (age < 60) return 15;
//     if (age < 70) return 24;
//     return 32;
// };

// const calculateFemaleAgePoints = (age: number): number => {
//     if (age < 30) return 0;
//     if (age < 40) return 1;
//     if (age < 50) return 3;
//     if (age < 60) return 6;
//     if (age < 70) return 11;
//     return 18;
// };

// const addPainScore = (score: number, painLevel: number): number => {
//     return score * (1 + painLevel / 100);
// };

// const calculateChestPainPoints = (
//     pain: 'Cardiac' | 'Possibly Cardiac' | 'Non-Cardiac',
//     painLevel: number
// ): number => {
//     switch (pain) {
//         case 'Cardiac':
//             return addPainScore(15, painLevel);
//         case 'Possibly Cardiac':
//             return addPainScore(8, painLevel);
//         case 'Non-Cardiac':
//             return 0;
//     }
// };

const KAISER_URL = 'https://kp.org';

const calculateScore = (
  age: number,
  sex: 'male' | 'female',
  chestPain: 'Cardiac' | 'Possibly Cardiac' | 'Non-Cardiac',
  diabetes: boolean,
  hypertension: boolean,
  dyslipidemia: boolean,
  smoker: boolean,
  coronaryCalciumScoreAvailable?: boolean,
  coronaryCalciumScore?: number,
): number => {
  const intercept = -5.63;
  const beta_age = 0.0615;
  const beta_male = 1.332;
  const beta_atypical = 0.631;
  const beta_typical = 1.996;
  const beta_diabetes = 0.829;
  const beta_interaction_typical_diabetes = -0.405;
  const beta_hypertension = 0.336;
  const beta_dyslipidemia = 0.425;
  const beta_smoker = 0.464;
  const beta_ln_calcium = 0.45;

  const is_male = sex.toLocaleLowerCase() === 'male' ? 1 : 0;
  const symptom_typical = chestPain === 'Cardiac' ? 1 : 0;
  const symptom_atypical = chestPain === 'Possibly Cardiac' ? 1 : 0;
  const d = diabetes ? 1 : 0;

  let Z = intercept
    + beta_age * age
    + beta_male * is_male
    + beta_typical * symptom_typical
    + beta_atypical * symptom_atypical
    + beta_diabetes * d
    + beta_interaction_typical_diabetes * (symptom_typical * d)
    + beta_hypertension * (hypertension ? 1 : 0)
    + beta_dyslipidemia * (dyslipidemia ? 1 : 0)
    + beta_smoker * (smoker ? 1 : 0);

    if (coronaryCalciumScoreAvailable) {
        const ccs = coronaryCalciumScore || 0;
        const ln_ccs = Math.log(ccs + 1);
        Z += beta_ln_calcium * ln_ccs;
    }

    const probability = 1 / (1 + Math.exp(-Z));
    return Math.round(probability * 100);
};

const calculateEnhancedScore = (
  age: number,
  sex: 'male' | 'female',
  chestPain: 'Cardiac' | 'Possibly Cardiac' | 'Non-Cardiac',
  painLevel: number,
  diabetes: boolean,
  hypertension: boolean,
  dyslipidemia: boolean,
  smoker: boolean,
  familyHistory: boolean,
  coronaryCalciumScoreAvailable?: boolean,
  coronaryCalciumScore?: number,
): number => {
  const intercept = -5.63;
  const beta_age = 0.0615;
  const beta_male = 1.332;
  const beta_atypical = 0.631;
  const beta_typical = 1.996;
  const beta_diabetes = 0.829;
  const beta_interaction_typical_diabetes = -0.405;
  const beta_hypertension = 0.336;
  const beta_dyslipidemia = 0.425;
  const beta_smoker = 0.464;
  const beta_ln_calcium = 1.545;
  const beta_pain_level = 0.1;
  const beta_family_history = 0.69;

  const is_male = sex === 'male' ? 1 : 0;
  const symptom_typical = chestPain === 'Cardiac' ? 1 : 0;
  const symptom_atypical = chestPain === 'Possibly Cardiac' ? 1 : 0;
  const d = diabetes ? 1 : 0;

  let Z = intercept
    + beta_age * age
    + beta_male * is_male
    + beta_typical * symptom_typical
    + beta_atypical * symptom_atypical
    + beta_diabetes * d
    + beta_interaction_typical_diabetes * (symptom_typical * d)
    + beta_hypertension * (hypertension ? 1 : 0)
    + beta_dyslipidemia * (dyslipidemia ? 1 : 0)
    + beta_smoker * (smoker ? 1 : 0)
    + beta_pain_level * painLevel
    + beta_family_history * (familyHistory ? 1 : 0);

  if (coronaryCalciumScoreAvailable) {
    const ccs = coronaryCalciumScore || 0;
    Z += beta_ln_calcium * Math.log(ccs + 1); 
  }

  const probability = 1 / (1 + Math.exp(-Z));
  return Math.round(probability * 100);
};


const getScorePosition = (score: number) => {
    const position = 100 - score;
    if (position < 0.5) return 0.5;
    if (position > 96.3) return 96.3;
    return position;
};

const PAIN_LEVEL_MARKS = [
    { value: 0, label: 0 },
    { value: 5, label: 5 },
    { value: 10, label: 10 },
];

const getRecommendationForScore = (
    score: number,
    canExercise?: 'yes' | 'no',
    wasECGWithin6Months?: 'yes' | 'no',
    wasECGInterpretable?: 'yes' | 'no',
    ctaExpertiseAvailable?: 'yes' | 'no',
    age?: number,
): string => {
    if (score < 1) return '';
    if (score <= 15) return 'XRay (optional)';
    if (score >= 85) return 'Calling Cardiologist urgently';

    const ageValue = age || 0;

    if (canExercise === 'no') {
        if (ctaExpertiseAvailable === 'yes') return 'CTA';
        return 'Pharmacologic Stress Test (MPI or stress echo)';
    }

    if (wasECGWithin6Months === 'no') return 'ECG in HealthConnect';
    if (wasECGInterpretable === 'yes') return 'Treadmill Stress Test';
    if (ageValue < 70) return 'Pharmacologic Stress Test (MPI or stress echo)';
    if (ctaExpertiseAvailable === 'no') return 'Pharmacologic Stress Test (MPI or stress echo)';

    return 'CTA';
};

const getRecommendationSummary = (
    age: number,
    sex: 'male' | 'female',
    chestPain: 'cardiac' | 'possibly-cardiac' | 'non-cardiac',
    diabetes: boolean,
    hypertension: boolean,
    dyslipidemia: boolean,
    smoker: boolean,
    coronaryCalciumScoreAvailable: boolean,
    coronaryCalciumScore: number,
    score: number,
    recommendation: string,
): string => {
    let ageSummary = age > 0 ? `${age} years old ` : '';
    let sexSummary = sex === 'male' ? 'Male' : 'Female';
    let chestPainSummary = '';
    if (chestPain === 'cardiac') chestPainSummary = ' who has typical chest pain.';
    else if (chestPain === 'possibly-cardiac') chestPainSummary = ' who has atypical chest pain.';

    const risks = [];
    if (diabetes) risks.push('Diabetes');
    if (hypertension) risks.push('Hypertension');
    if (dyslipidemia) risks.push('Dyslipidemia');
    if (smoker) risks.push('Current or Past Smoker');

    const risksSummary = risks.length > 0 ? ' Cardiac risk factors: ' + risks.join(', ') + '.' : '';

    let coronaryCalciumScoreSummary = '';
    if (coronaryCalciumScoreAvailable && coronaryCalciumScore > 0) {
        coronaryCalciumScoreSummary = ` Coronary Calcium: ${coronaryCalciumScore}.`;
    }

    let scoreLvl = 'Low';
    if (score > 15) scoreLvl = 'Intermediate';
    if (score > 85) scoreLvl = 'High';
    const scoreSummary = ` The calculated pre-test probability of CAD is ${score}%. This is consistent with ${scoreLvl} pre-test Probability.`;

    const recommendationSummary = ` The recommended course of action: Consider ${recommendation}. (Based on current TPMG and AHA/ACC guidelines.)`;

    return `${ageSummary}${sexSummary}${chestPainSummary}${risksSummary}${coronaryCalciumScoreSummary}${scoreSummary}${recommendationSummary}`;
};

const getScoreDisplayValue = (score: number): string => {
    if (score < 15) return '<15%';
    if (score < 50) return '15-50%';
    if (score < 85) return '50-85%';
    return '>85%';
};

export {
    KAISER_URL,
    PAIN_LEVEL_MARKS,
    calculateScore,
    calculateEnhancedScore,
    getRecommendationForScore,
    getRecommendationSummary,
    getScorePosition,
    getScoreDisplayValue,
};
